<template>
  <div :class="{
    'cp-main__container': isCustomerPortal && isSessionUserKnown,
    'main__container': session && !session.is_legacy,
    'no__subtitle': session && !session.subtitle && session.is_legacy
  }">
    <Breadcrumbs v-if="showBreadcrumbs" :breadcrumbs-info="breadcrumbs" :ignore-current="true"></Breadcrumbs>
    <b-modal
      :visible="!!warning"
      id="warning"
      centered
      hide-footer
      hide-header
      size="lg"
      @hide="onCloseWarning()"
    >
      <b-row class="alert-messages">
        <div class="alert-container" v-bind:class="{'mt-0': !session}">
          <div>{{warning}}</div>
        </div>
      </b-row>
      <b-link block class="centered-block close-button" variant="secondary" @click="closeWarning()">&times;</b-link>
    </b-modal>
    <b-modal
      :visible="!!callCenterPopupContent"
      id="call-center-popup"
      centered
      hide-footer
      hide-header
      size="lg"
      @hide="onCloseCallCenterPopup()"
    >
      <template v-if="!!callCenterPopupContent">
        <div v-html="callCenterPopupContent"></div>
      </template>
      <b-link block class="centered-block close-button" variant="secondary" @click="closeCallCenterPopup()">&times;</b-link>
    </b-modal>
    <div v-if="showHeading" class="cp-main__title">
      <p class="container"><span v-html="session.welcome_message"></span></p>
    </div>
    <SessionContainer v-if="!callCenterPopupContent">
      <template v-slot="{session}">
        <div id="choices" class="flex" :class="{'content-confirmation': isConfirmationPopup}">
          <template v-if="!isSessionValid">
            <SessionComplete>You have been logged out due to inactivity.</SessionComplete>
          </template>
          <template v-else-if="needsCustomerMatch(session)">
            <CustomerMatch @back="goBack" @done="updateInputState(!isCustomerPortal)" @fail="onCustomerMatchFailed"></CustomerMatch>
          </template>
          <template v-else-if="needsCustomerVerification(session)">
            <CustomerVerify @back="goBack" @done="updateInputState(!isCustomerPortal)"></CustomerVerify>
          </template>
          <template v-else-if="needsRMAItems(session)">
            <SelectActionItems
              label="Submit"
              :defaults="inputData"
              :input="selectedInput"
              @select="selectItemsForRMAOrCancel"
              @back="goBack"
            ></SelectActionItems>
          </template>
          <template v-else-if="needsRebillItems(session)">
            <SelectActionItems
              rebill
              label="Submit"
              :defaults="inputData"
              :input="selectedInput"
              @select="selectItemsForRMAOrCancel"
              @back="goBack"
            ></SelectActionItems>
          </template>
          <template v-else-if="needsVariants(session)">
            <Variants
              :item="currentSelectedItem"
              :new-campaign-product="selectedCampaignProduct"
              :quantity="(inputData && inputData['quantity']) ? inputData['quantity'] : currentSelectedItem.quantity"
              :subscription-only="subscriptionOnly"
              @select="selectVariants"
              @back="goBack"
            ></Variants>
          </template>
          <template v-else-if="isCustomerPortal">
            <CustomerPortal
              :input="selectedInput"
              :inputType="inputType"
              @submit="selectInput"
              @selectItemInput="selectItemInput"
            ></CustomerPortal>
          </template>
          <template v-else-if="isItemStatus(session)">
            <OrderItems
              :key="'session-' + selectedInput.id.toString()"
              :input="selectedInput"
              :items="currentSelectedItem ? [currentSelectedItem] : []"
              @submit="selectInput"
              @back="goBack"
              @edit="editItem"
            >
              <SessionContent></SessionContent>
            </OrderItems>
          </template>
          <template v-else-if="isCheckout">
            <Checkout @submit="submitInputAndData" @back="goBack"></Checkout>
          </template>
          <template v-else-if="isInputActionStatus(session)">
            <InputActionStatus :input="selectedInput" @done="submit" @back="goBack" @edit="editItem"></InputActionStatus>
          </template>
          <template v-else-if="isCheckoutConfirm">
            <CheckoutConfirm :input="selectedInput" @done="submit" @back="goBack"></CheckoutConfirm>
          </template>
          <template v-else-if="isSessionComplete">
            <SessionComplete><div v-html="thankYouMessage"></div></SessionComplete>
          </template>
          <template v-else-if="isCampaignSelect">
            <SelectCampaign :input="selectedInput" @select="submitInputData" @back="goBack"></SelectCampaign>
          </template>
          <template v-else-if="isSelectedProductRequired(session)">
            <SelectExchangeProduct
              :item="selectedItem"
              :subscription-only="subscriptionOnly"
              :quantity-type="quantityType"
              :current-session="session"
              @select="submitInputAndData"
              @back="goBack"
            ></SelectExchangeProduct>
          </template>
          <template v-else-if="isItemSelection">
            <div>
              <SessionContent></SessionContent>
              <SelectItems
                label="Continue"
                item-select-label="Get Help"
                :defaults="selectedItems"
                :input="selectedInput"
                @select="onItemsSelected"
                @selectItemInput="selectItemInput"
                @back="goBack"
              ></SelectItems>
            </div>
          </template>
          <template v-else-if="isOrderTracking">
            <OrderTracking :item="session.selected_item" :fulfillments="fulfillments" @clear="goBack" />
          </template>
          <template v-else>
            <div class="inputs" v-if="canDisplayTitle">
              <StepTitle>
                <div v-if="getSessionTitle(session)" v-html="getSessionTitle(session)" class="how-can label-content-width"></div>
                <template v-if="session && !session.enhanced_mode && session.subtitle" #subtitle><div v-html="getSessionSubTitle(session)" class="option"></div></template>
                <template v-if="session && session.content && session.content.length" #content>
                  <div :class="{'page-content': session.enhanced_mode}" v-html="getSessionContent(session)"></div>
                </template>
                <template v-if="showSelectedProduct" #product>
                  <table class="selected-item-table selected-item">
                    <tr>
                      <td v-if="selectedItems.length > 1">{{selectedItemIndex + 1}}/{{selectedItems.length}}</td>
                      <td><img :src="currentSelectedItemImageUrl" alt=""></td>
                      <td class="product-name">{{currentSelectedItem.quantity}} &times; <span v-html="currentSelectedItem.name"></span></td>
                    </tr>
                  </table>
                </template>
                <template #static-data>
                  <StepMedia :session="session" />
                </template>
              </StepTitle>
              <template v-if="session && (!session.enhanced_mode || !session.content)">
                <div v-if="needsInputFields()">
                  <InputFields @back="goBack" :fields="selectedInput.input_fields" :item="selectedItem" @submit="submitInputData"></InputFields>
                </div>
                <div v-else-if="isCustomerInfo">
                  <CustomerInfo :input="selectedInput" @submit="selectInput" @back="goBack"></CustomerInfo>
                </div>
                <div v-else-if="isPaymentUpdater">
                  <PaymentUpdater :rebill="rebill" :input="selectedInput" @submit="selectInput" @back="goBack"></PaymentUpdater>
                </div>
                <div v-else-if="isPaymentInfo">
                  <PaymentInfo :input="selectedInput" @submit="selectInput" @back="goBack"></PaymentInfo>
                </div>
                <div v-else-if="isRadioGroup(session) || isSelect(session) || isRadioOrSelect(session)">
                  <ChoiceFields @submit="selectInput" @back="goBack" :key="session ? session.step : 0"></ChoiceFields>
                </div>
                <div v-else-if="isButtonGroup(session)">
                  <div v-if="isIconButtons" class="home-icons" :class="{'wrapper': isBoxButton}">
                    <b-row class="justify-content-sm-center boxes icon-buttons">
                      <template v-if="isBoxButton">
                        <b-col cols="6" sm="12" v-for="input in session.valid_inputs" :key="'mob-choice-' + input.id" @click="selectInput(input)" class="choice d-block d-sm-none">
                          <div class="box" :class="getCustomClass(input)" v-lazy-container="{ selector: 'img' }">
                            <img :data-src="input.icon.file" alt="" block variant="primary"/>
                            <h2 v-html="input.formatted_label"></h2>
                          </div>
                        </b-col>
                      </template>
                      <b-col cols="12" sm="auto" v-for="input in session.valid_inputs" :key="'choice-' + input.id" @click="selectInput(input)" class="choice" :class="{'d-none d-sm-block': isBoxButton}">
                        <div class="box hide-on-phone" :class="getCustomClass(input)" v-lazy-container="{ selector: 'img' }">
                          <img :data-src="input.icon.file" alt="" block variant="primary"/>
                          <h2 v-html="input.formatted_label"></h2>
                        </div>
                        <b-button block
                                  class="d-sm-none btn btn-default btn-session gutters mob-home-btn"
                                  :class="getCustomClass(input)"
                                  variant="primary">
                          <img :src="input.icon.file" alt="" block variant="primary" class="mob-home-btn-icon"/>
                          <p v-html="input.formatted_label" class="new-btns"></p>
                        </b-button>
                      </b-col>
                    </b-row>
                  </div>
                  <div v-else>
                    <b-row class="justify-content-md-center boxes claim">
                      <b-col :md="singleColumnButtons && session.is_legacy ? 8 : 9" :lg="singleColumnButtons && session.is_legacy ? 6 : 7" class="my-class">
                        <b-row class="choice-fields-btns">
                          <b-col v-for="input in session.valid_inputs" :key="'choice-' + input.id" :md="singleColumnButtons ? 12 : 6" class="text-center">
                            <InputButton :input="input" @click="selectInput(input)"></InputButton>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </div>
                  <StepBackButton @back="goBack" :single-column-buttons="singleColumnButtons" />
                </div>
                <div v-else-if="session && (!session.is_legacy || session.is_step_preview)">
                  <b-row class="justify-content-md-center boxes welcome choice-fields-btns">
                    <b-col v-for="input in session.valid_inputs" :key="'choice-' + input.id" :md="session.is_popup ? 5 : 4" :lg="session.is_popup ? 4 : 3" class="text-center">
                      <button @click="selectInput(input)"
                              class="btn-base input-choice-btn"
                              :class="getCustomClass(input, true)"
                              :style="getStyles(input)"
                      ><div v-html="sessionInputLabel(input)"></div>
                      </button>
                    </b-col>
                    <b-col v-for="input in (session.additional_inputs || [])" :key="'choice-' + input.id" :md="session.is_popup ? 5 : 4" :lg="session.is_popup ? 4 : 3" class="text-center">
                      <button @click="selectInput(input)"
                              class="btn-base input-choice-btn new-btn-outline-success"
                              :class="getCustomClass(input, true)"
                              :style="getStyles(input)"
                      ><div v-html="sessionInputLabel(input)"></div>
                      </button>
                    </b-col>
                  </b-row>
                </div>
                <div v-else>
                  <b-row class="justify-content-md-center boxes welcome">
                    <b-col v-if="session && session.can_undo" md="4" lg="3">
                      <button type="button" @click="goBack()" class="btn-base input-choice-btn new-btn-outline-success stroke-2x">Back</button>
                    </b-col>
                    <b-col md="4" lg="3" class="mt-2 mt-md-0" v-if="showFinalSubmitButton(session)">
                      <button type="button" @click="submitForm()" class="btn-base input-choice-btn stroke-2x">Submit</button>
                    </b-col>
                  </b-row>
                </div>
              </template>
              <template v-else>
                <b-row v-if="session && session.can_undo" class="justify-content-md-center boxes welcome">
                  <b-col md="4" lg="3">
                    <button type="button" @click="goBack()" class="btn-base input-choice-btn new-btn-outline-success stroke-2x">Back</button>
                  </b-col>
                </b-row>
              </template>
            </div>
          </template>
        </div>
        <b-link v-if="canShowCloseIcon" block class="centered-block close-button" @click="closePopup()" variant="secondary">&times;</b-link>
      </template>
    </SessionContainer>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState, mapGetters } from 'vuex'
import {
  SessionStatus,
  InputType,
  InputState,
  VariantType,
  MobileIconButtonType,
  StepButtonType,
  CustomerPortalInputType,
  QuantityType,
  ShowBreadcrumbs,
  MFAStatus
} from '@/constants'
import {
  getCustomInputClass,
  getItemImageUrl,
  getErrorData,
  isFirstInputDone,
  setFirstInputDone,
  updateFontSizes,
  recalculateFontSizes,
  getPlaceholderImageUrl,
  isItemSelectionInput,
  getTrimmedValue,
  getStepInfo,
  selectLanguage,
  scrollToTop
} from '@/utils'

const StepTitle = () => import('./StepTitle')
const StepMedia = () => import('./StepMedia')
const SessionContent = () => import('./SessionContent')
const SessionContainer = () => import('./SessionContainer')
const SessionComplete = () => import('@/components/SessionComplete')
const OrderItems = () => import('@/components/OrderItems')
const CustomerInfo = () => import('@/components/CustomerInfo')
const PaymentInfo = () => import('@/components/PaymentInfo')
const PaymentUpdater = () => import('@/components/PaymentUpdater')
const SelectItems = () => import('@/components/SelectItems')
const CustomerMatch = () => import('@/components/CustomerMatch')
const CustomerVerify = () => import('@/components/CustomerVerify')
const InputActionStatus = () => import('@/components/InputActionStatus')
const InputFields = () => import('@/components/InputFields')
const SelectExchangeProduct = () => import('@/components/SelectExchangeProduct')
const ChoiceFields = () => import('@/components/ChoiceFields')
const SelectActionItems = () => import('@/components/SelectActionItems')
const SelectCampaign = () => import('@/components/SelectCampaign')
const Variants = () => import('@/components/Variants')
const Checkout = () => import('@/components/Checkout')
const CheckoutConfirm = () => import('@/components/CheckoutConfirm')
const Breadcrumbs = () => import('@/components/Breadcrumbs')
const StepBackButton = () => import('@/components/StepBackButton')
const CustomerPortal = () => import('@/components/CustomerPortal/CustomerPortal')
const OrderTracking = () => import('@/components/Order/OrderTracking')
const InputButton = () => import('@/components/InputButton')

export default {
  name: 'Session',
  components: {
    StepMedia,
    StepBackButton,
    Breadcrumbs,
    SessionContainer,
    SessionContent,
    StepTitle,
    SessionComplete,
    OrderItems,
    CustomerInfo,
    PaymentInfo,
    PaymentUpdater,
    SelectItems,
    CustomerMatch,
    CustomerVerify,
    InputActionStatus,
    InputFields,
    SelectExchangeProduct,
    ChoiceFields,
    SelectActionItems,
    SelectCampaign,
    Variants,
    Checkout,
    CheckoutConfirm,
    CustomerPortal,
    OrderTracking,
    InputButton
  },
  data: function() {
    return {
      selectedInput: null,
      selectedIntent: null,
      userSelectedInput: null,
      selectedItem: null,
      updatedItem: null,
      isItemSelection: false,
      inputType: InputType.Choice,
      submitting: false,
      inputData: null,
      statusMessages: [],
      callCenterPopupContent: null,
      itemSelectionState: InputState.Unknown,
      checkForVariants: false,
      selectedCampaignProduct: null,
      warning: null,
      rebill: false,
      hasSlotContent: false,
      breadcrumbs: null,
      subscriptionOnly: false,
      variantSelected: false,
      quantityType: QuantityType.Any
    }
  },
  computed: {
    ...mapState([
      'session', 'firstLogin', 'selectedItems', 'selectedItemIndex', 'inputState', 'refreshSessionOnUndo',
      'styleVariables', 'isLoading', 'fulfillments', 'campaign', 'popupSession', 'stepStack'
    ]),
    ...mapGetters(['canGotoPreviousItem', 'currentSelectedItem', 'isSessionValid', 'isSessionComplete',
      'isLifelineActive', 'isSessionUserKnown', 'canShowCloseIcon', 'popupCloseCanUndo', 'isMainMenu',
      'popupCloseCanSelectFirstInput', 'popupCloseCanSelectLastInput', 'isAlreadySessionUrl'
    ]),
    showHeading() {
      return this.isSessionUserKnown && this.isCustomerPortal && this.campaign.portal_settings && this.session && this.session.welcome_message
    },
    canDisplayTitle: function() {
      return !this.isFAQs && !this.isProductInfo
    },
    isFAQs: function() {
      return (this.inputType === InputType.Faqs || (this.session && this.session.step_type === InputType.Faqs))
    },
    isCustomerInfo: function() {
      return ([InputType.BillingAddress, InputType.ShippingAddress].indexOf(this.inputType) >= 0)
    },
    isPaymentInfo: function() {
      return (this.inputType === InputType.PaymentInfo && !this.rebill)
    },
    isPaymentUpdater: function() {
      return this.inputType === InputType.PaymentInfo && this.rebill
    },
    isProductInfo: function() {
      return (this.inputType === InputType.Product)
    },
    isCheckout: function() {
      return (this.inputType === InputType.Checkout || (this.session && this.session.step_type === InputType.Checkout))
    },
    isCheckoutConfirm: function() {
      return (this.inputType === InputType.CheckoutConfirm)
    },
    isCustomerPortal: function() {
      return this.session && (
        Object.values(CustomerPortalInputType).indexOf(this.session.step_type) > -1 ||
        Object.values(CustomerPortalInputType).indexOf(this.inputType) > -1
      )
    },
    isCampaignSelect: function() {
      return (this.inputType === InputType.SelectCampaign)
    },
    isCustomerMatch: function() {
      return (this.inputType === InputType.MatchCustomer)
    },
    isOrderTracking: function() {
      return this.session && this.session.step_type === InputType.ShipmentTracking
    },
    currentSelectedItemImageUrl: function() {
      if (this.currentSelectedItem) {
        return getItemImageUrl(this.currentSelectedItem, 'small')
      }

      return getPlaceholderImageUrl('small')
    },
    isIconButtons: function() {
      if (this.session.valid_inputs.length) {
        for (let i = 0; i < this.session.valid_inputs.length; i++) {
          if (!this.session.valid_inputs[i].icon) {
            return false
          }
        }

        return true
      }

      return false
    },
    hasLink: function() {
      if (this.session.valid_inputs.length) {
        for (let i = 0; i < this.session.valid_inputs.length; i++) {
          if (this.isLink(this.session.valid_inputs[i])) {
            return true
          }
        }
      }

      return false
    },
    singleColumnButtons: function() {
      return (this.session.valid_inputs.length < 6) || this.hasLink
    },
    thankYouMessage: function() {
      if (this.session && this.session.thank_you_message && this.session.thank_you_message.length) {
        return this.session.thank_you_message
      }

      return 'You have been logged out.'
    },
    isInputDataEditable: function() {
      let isEditable = false

      if (this.selectedInput && this.selectedInput.input_fields) {
        const fields = Object.values(this.selectedInput.input_fields)

        for (const field of fields) {
          if (field.type !== InputType.Hidden) {
            isEditable = true
            break
          }
        }
      }

      return isEditable
    },
    isBoxButton: function() {
      const mobileIconButtonType = (this.session && this.session.mobile_icon_button_type) ||
        (this.styleVariables && this.styleVariables.mobile_icon_button_type)

      if (mobileIconButtonType) {
        return +mobileIconButtonType === MobileIconButtonType.Box
      }
      return false
    },
    showBreadcrumbs: function() {
      if (!this.isMainMenu && !this.isCustomerPortal && this.session && this.session.campaign &&
          (this.session.campaign.show_breadcrumbs !== ShowBreadcrumbs.Never) &&
          !this.needsCustomerMatch(this.session) && !this.needsCustomerVerification(this.session)) {
        if (this.isCustomerInfo || this.isPaymentInfo || this.isUpdateInfoStep() ||
            (this.stepStack && this.stepStack.length > 0)) {
          return (this.session.campaign.show_breadcrumbs === ShowBreadcrumbs.Always) || this.session.can_undo
        }
      }

      return false
    },
    requiresItem: function() {
      return this.session && this.session.valid_inputs && this.session.valid_inputs.some(input => input.requires_item)
    },
    showSelectedProduct: function() {
      return this.session &&
        this.session.is_legacy &&
        !this.session.enhanced_mode &&
        this.selectedItems.length &&
        this.currentSelectedItem &&
        (!this.session.is_main_funnel || this.requiresItem)
    },
    isConfirmationPopup: function() {
      return this.popupSession && this.popupSession.step_type === InputType.Confirmation
    },
    canAddToBreadcrumbs: function() {
      return !this.isMainMenu && !this.isFAQs && !this.isCustomerMatch
    }
  },
  methods: {
    closePopup: function() {
      if (this.popupCloseCanUndo) {
        this.goBack()
      } else if (this.popupCloseCanSelectFirstInput) {
        this.selectInput(this.popupSession.valid_inputs[0])
      } else if (this.popupCloseCanSelectLastInput) {
        this.selectInput(this.popupSession.valid_inputs[this.session.valid_inputs.length - 1])
      }
    },
    isUpdateInfoStep: function(session) {
      session = session || this.session
      if (!session || !session.step_name) {
        return false
      }
      return ['update your info', 'update your information'].indexOf(session.step_name.toLowerCase()) > -1
    },
    populateBreadcrumbs: function(session) {
      if (this.showBreadcrumbs) {
        // Always start with the Main Menu
        const breadcrumbs = [
          {label: 'Main Menu', path: {name: 'session'}, home: true}
        ]
        const crumbIndexMap = {}

        // Get the step stack from the store
        const stepStack = this.stepStack

        // Add breadcrumbs for each step in the stack
        if (stepStack && stepStack.length > 0) {
          stepStack.forEach((stepInfo, index) => {
            // For each step in the stack, create a breadcrumb that will undo the appropriate number of times
            const isCurrent = index === stepStack.length - 1
            const crumb = {
              label: stepInfo.name,
              slug: stepInfo.slug,
              undoCount: stepStack.length - index - 1,
              path: { name: 'session', params: { step: stepInfo.id } },
              back: (undoCount) => this.processUndo(undoCount),
              current: isCurrent
            }

            if (stepInfo.id in crumbIndexMap) {
              // crumb already exists, so update it and remove anything after it
              breadcrumbs.splice(crumbIndexMap[stepInfo.id], Number.POSITIVE_INFINITY, crumb)
            } else {
              // new crumb, so add it
              crumbIndexMap[stepInfo.id] = breadcrumbs.length
              breadcrumbs.push(crumb)
            }
          })
        } else if (session && session.step_name && !this.isMainMenu) {
          // Fallback for current step if stack is empty
          breadcrumbs.push({
            label: session.step_name,
            path: { name: 'session', params: { step: session.step_id } },
            home: false,
            current: true
          })
        }

        this.breadcrumbs = breadcrumbs
      }
    },
    processUndo: function(count) {
      this.goBack(count)
    },
    closeCallCenterPopup: function() {
      this.$bvModal.hide('call-center-popup')
    },
    onCloseCallCenterPopup: function() {
      this.callCenterPopupContent = null

      if (this.isInputActionStatus(this.session && this.session.is_popup ? this.popupSession : this.session)) {
        this.submit()
      }
    },
    closeWarning: function() {
      this.$bvModal.hide('warning')
    },
    onCloseWarning: function() {
      // refresh the session data
      axios.get('/sessions/')
        .then(resp => {
          this.$store.commit('session', resp.data)
        })
        .catch(err => {
          this.$store.commit('critical_error', err)
        })
        .finally(() => this.init())
    },
    sessionInputLabel(input) {
      if (input) {
        return updateFontSizes(input.formatted_label)
      }
      return null
    },
    isLink: function(input) {
      return input.type === InputType.Link ||
        (input.button_styles && input.button_styles.style_type === StepButtonType.TextOnly)
    },
    getCustomClass: function(input, includeButtonClass = false) {
      return getCustomInputClass(this.session, input, includeButtonClass)
    },
    getStyles: function(input) {
      return input && input.button_styles && input.button_styles.style
    },
    selectInput: function(input, data, messages) {
      this.selectedInput = input
      if (data) {
        if (!data.updatePaymentInfo) {
          this.userSelectedInput = input
        }
        delete data['updatePaymentInfo']
      }

      if (typeof data !== 'undefined') {
        this.inputData = data
      }

      if (typeof messages !== 'undefined') {
        this.statusMessages = messages
      }

      console.log({input: input, data: data})

      this.submitForm()
    },
    submitInputAndData: function(input, data, selectedCampaignProduct) {
      this.selectedInput = input
      this.userSelectedInput = input
      this.selectedCampaignProduct = selectedCampaignProduct
      this.submitInputData(data)
    },
    clearInput: function() {
      if ((this.session && this.session.step_type === InputType.EnterTroubleshooter) &&
          (this.selectedInput && this.selectedInput.type === InputType.PathNotFound)) {
        // path not found (i.e. no troubleshooter exists for product) for troubleshooter required
        // data input, going back from the data input should skip back past the (selected) path not found input
        if (this.session.can_undo) {
          this.goBack()
        }
      } else {
        this.selectedInput = null
        this.userSelectedInput = null
        this.selectedIntent = null
        this.updateInputState(false)
      }
    },
    selectItemInput: function(data) {
      this.selectedInput = data.input
      this.onItemsSelected([data.item], data.intent)
    },
    onItemsSelected: function(items, intent) {
      this.$store.commit('set_selected_items', items)
      this.selectedItem = items[0]
      this.selectedIntent = intent
      this.submit()
    },
    selectItemsForRMAOrCancel: function(data, selectedItem) {
      if (selectedItem) {
        this.selectedItem = selectedItem
      }

      this.itemSelectionState = InputState.Unknown
      this.submitInputData(data)
    },
    selectVariants: function(data) {
      if (this.inputData) {
        data = Object.assign({}, this.inputData, data)
      }

      this.checkForVariants = false
      this.selectedCampaignProduct = null
      this.subscriptionOnly = false
      this.quantityType = QuantityType.Any
      this.variantSelected = true
      this.submitInputData(data)
    },
    submitInputData: function(data) {
      this.inputData = data
      let mustSelectVariants = false

      if ((this.selectedCampaignProduct && (this.selectedCampaignProduct.show_variants)) ||
        (this.checkForVariants && this.currentSelectedItem && this.inputData &&
          (this.currentSelectedItem.product.variant_type !== VariantType.None))) {
        if (!this.inputData || !this.inputData['variants'] || !this.inputData['variants'].length) {
          mustSelectVariants = true
        }
      }

      this.checkForVariants = false

      if (this.itemSelectionState === InputState.RMAItems) {
        this.itemSelectionState = InputState.Unknown
        this.$store.commit('set_input_state', InputState.RMAItems)
      } else if (this.itemSelectionState === InputState.RebillItems) {
        this.itemSelectionState = InputState.Unknown
        this.$store.commit('set_input_state', InputState.RebillItems)
      } else if (mustSelectVariants) {
        this.$store.commit('set_input_state', InputState.Variants)
      } else {
        this.$store.commit('set_input_state', InputState.OkToSubmit)
        this.submit()
      }
    },
    submitForm: function() {
      if (this.selectedInput) {
        this.updateInputState(false)
        this.submit()
      }
    },
    editItem: function(input, item) {
      this.selectedInput = input
      this.userSelectedInput = input
      this.selectedItem = item
      this.submitForm()
    },
    onCustomerMatchFailed: function(message) {
      if (this.isCustomerMatch && this.session.additional_inputs && this.session.additional_inputs.length) {
        this.selectInput(this.session.additional_inputs[0]) // customer match step - select no customer found input
      } else {
        this.$store.commit('no_match', message)
      }
    },
    updateInputState: function(resubmit) {
      if (this.selectedInput) {
        let inputState = InputState.OkToSubmit

        if (this.selectedInput.requires_customer) {
          if (this.session && this.session.status === SessionStatus.Unmatched) {
            inputState = InputState.NeedsMatch
          } else if (this.session && (
            (this.session.status === SessionStatus.Unverified) ||
            (this.session.status === SessionStatus.Matched) ||
            (this.session.mfa_status === MFAStatus.Pending)
          )) {
            inputState = InputState.NeedsVerification
          }
        }

        if (inputState === InputState.OkToSubmit) {
          this.itemSelectionState = InputState.Unknown
          this.checkForVariants = false

          if (this.selectedInput.input_fields) {
            const keys = Object.keys(this.selectedInput.input_fields)

            if (keys && keys.length) {
              for (const key of keys) {
                const field = this.selectedInput.input_fields[key]
                const fieldType = field.type
                const defaultVal = field.default

                if (key === 'variants') {
                  this.checkForVariants = true

                  if (field.subscription_only) {
                    this.subscriptionOnly = true
                  }
                } else if (!this.selectedItem) {
                  if ((key === 'items') && (fieldType === InputType.ActiveItem)) {
                    this.itemSelectionState = InputState.RMAItems // select items for RMA
                  } else if ((key === 'items') && (fieldType === InputType.RebillItem)) {
                    this.itemSelectionState = InputState.RebillItems // select items for rebill

                    if (this.selectedInput.type === InputType.PaymentInfo) {
                      if (!this.userSelectedInput) {
                        inputState = InputState.OkToSubmit
                        this.rebill = true
                      } else {
                        inputState = InputState.RebillItems
                      }
                    }
                  } else if ((fieldType !== InputType.Product) && (fieldType !== InputType.Hidden) && !defaultVal) {
                    if (!this.isRadioGroup(this.session) && !this.isSelect(this.session) &&
                      !this.isRadioOrSelect(this.session)) {
                      inputState = InputState.InputFields // form input fields
                    }
                  }
                }
              }

              if (inputState === InputState.OkToSubmit) {
                if (this.itemSelectionState === InputState.RMAItems) {
                  inputState = InputState.RMAItems
                } else if (this.checkForVariants) {
                  if (this.currentSelectedItem &&
                    (this.currentSelectedItem.product.variant_type !== VariantType.None)) {
                    inputState = InputState.Variants
                  }
                }
              }
            }
          }
        } else {
          this.$store.commit('hide_welcome_message')
        }

        if ([InputState.RMAItems].indexOf(inputState) > -1 && !this.refreshSessionOnUndo) {
          this.$store.commit('refresh_session_on_undo', true)
        }

        this.$store.commit('set_input_state', inputState)
        if (resubmit) {
          if (this.isOkToResubmit()) {
            console.log('resubmit')
            this.submit()
          } else {
            this.submitting = true // we selected an input, but not ok to submit yet (i.e. needs customer verification)
            this.$store.commit('ready')
          }
        } else if (this.isCustomerPortal) {
          this.$store.commit('ready')
        }
      } else {
        this.$store.commit('set_input_state', InputState.Unknown)
      }

      if (!this.isLifelineActive && this.session && this.session.is_popup) {
        this.$store.commit('set_display_state', null)
      }
    },
    needsInputFields: function() {
      return (this.inputState === InputState.InputFields)
    },
    needsRMAItems: function(session) {
      if (session && session.is_background_session) {
        return false
      }
      return (this.inputState === InputState.RMAItems)
    },
    needsRebillItems: function(session) {
      if (session && session.is_background_session) {
        return false
      }
      return (this.inputState === InputState.RebillItems)
    },
    needsVariants: function(session) {
      if (session && session.is_background_session) {
        return false
      }
      return (this.inputState === InputState.Variants)
    },
    needsCustomerMatch: function(session) {
      if (session && session.is_background_session) {
        return false
      }
      return (this.inputState === InputState.NeedsMatch)
    },
    needsCustomerVerification: function(session) {
      if (session && session.is_background_session) {
        return false
      }
      return (this.inputState === InputState.NeedsVerification)
    },
    isItemStatus: function(session) {
      if (this.session.is_popup && (session && !session.is_background_session)) {
        if (session.valid_inputs.length > 0) {
          let input = session.valid_inputs[0]
          this.selectedInput = input
          return input.type === InputType.ItemStatus
        }
      }
      return (this.inputType === InputType.ItemStatus)
    },
    isInputActionStatus: function(session) {
      if (this.session.is_popup && (session && !session.is_background_session)) {
        if (session.valid_inputs.length > 0) {
          let input = session.valid_inputs[0]
          if (input.type === InputType.InputActionStatus) {
            this.selectedInput = input
            this.$store.commit('set_input_state', InputState.OkToSubmit)
            return true
          }
          return false
        }
      }
      return (this.inputType === InputType.InputActionStatus)
    },
    isRadioGroup: function(session) {
      if (this.session.is_popup && (session && !session.is_background_session)) {
        if (session.valid_inputs.length > 0) {
          let input = session.valid_inputs[0]
          return input.type === InputType.Radio
        }
      }
      return (this.inputType === InputType.Radio)
    },
    isSelect: function(session) {
      if (this.session.is_popup && (session && !session.is_background_session)) {
        if (session.valid_inputs.length > 0) {
          let input = session.valid_inputs[0]
          return input.type === InputType.Select
        }
      }
      return (this.inputType === InputType.Select)
    },
    isRadioOrSelect: function(session) {
      if (this.session.is_popup && (session && !session.is_background_session)) {
        if (session.valid_inputs.length > 0) {
          let input = session.valid_inputs[0]
          return input.type === InputType.RadioSelect
        }
      }
      return (this.inputType === InputType.RadioSelect)
    },
    isReadyToSubmit: function() {
      return (this.inputState === InputState.OkToSubmit)
    },
    isOkToResubmit: function() {
      return this.isReadyToSubmit() && (this.isCustomerMatch || (this.session.valid_inputs.length !== 1))
    },
    getDefaultData: function() {
      let data = {}

      if (this.selectedInput.input_fields) {
        const keys = Object.keys(this.selectedInput.input_fields)

        if (keys && keys.length) {
          for (const key of keys) {
            if ('default' in this.selectedInput.input_fields[key]) {
              data[key] = this.selectedInput.input_fields[key].default
            }
          }
        }
      }

      return data
    },
    getShipmentReturnLabel: function(qrCode) {
      if (this.currentSelectedItem) {
        this.$store.commit('busy')
        let newWindow = window.open('', '_blank')
        this.$store.dispatch('load_shipment_return_label', {
          item: this.currentSelectedItem, qr_code: qrCode, newWindow})
      }
    },
    showLoader: function() {
      if (!this.isCustomerPortal) {
        if (this.selectedInput) {
          this.$root.$emit('toggle-processing-gifs', null, this.selectedInput.action_results)
        }
        this.$store.commit('busy')
      } else {
        this.$store.commit('showCPLoader')
      }
    },
    hideLoader: function() {
      if (this.selectedInput && this.selectedInput.processing_gif) {
        this.$root.$emit('toggle-processing-gifs', null)
      }
      this.$store.commit('ready')
      this.$store.commit('hideCPLoader')
    },
    submit: function() {
      if (!this.isReadyToSubmit() || this.session.is_step_preview) {
        this.submitting = true // we selected an input, but not ok to submit yet (i.e. needs customer verification)
        this.hideLoader()
      } else if (this.selectedInput.type === InputType.ShipmentReturnLabel) {
        this.getShipmentReturnLabel(false)
      } else if (this.selectedInput.type === InputType.ShipmentReturnQRCode) {
        this.getShipmentReturnLabel(true)
      } else {
        let params = {
          input: this.selectedInput.id
        }

        if (this.selectedItem) {
          params['item'] = this.selectedItem.id
        }

        if (
          this.selectedIntent &&
          isItemSelectionInput(this.selectedInput) &&
          this.session &&
          this.session.use_intents
        ) {
          params['intent'] = this.selectedIntent
        }

        if (this.inputData) {
          params['data'] = Object.assign(this.getDefaultData(), this.inputData)
        }

        this.showLoader()
        this.$store.dispatch('input', params)
          .then(session => {
            this.$store.commit('update_selected_item')
            console.log(session.step)

            // if we were passed some status messages to display, commit them so that they show up
            if (this.statusMessages && this.statusMessages.length) {
              this.$store.commit('messages', this.statusMessages)
            }
            if (this.selectedInput && this.selectedInput.type === InputType.Checkout) {
              this.$store.commit('set_cart', [])
            }

            // Update the step stack based on whether the step can be undone
            if (!session.can_undo) {
              this.$store.commit('clear_step_stack')
            }

            if (this.canAddToBreadcrumbs && this.isSessionValid) {
              this.$store.commit('push_to_step_stack', getStepInfo(session))
            }
            if (!this.refreshSessionOnUndo) {
              this.$router.push({ name: 'undo', params: { step: getTrimmedValue(session.step_slug) } }, () => {
                this.$router.push({ name: 'session', params: { step: getTrimmedValue(session.step_slug) } })

                if (this.isFAQs) {
                  this.$router.replace({name: 'faq-topics'})
                }
              }, (error) => console.log('ERROR', error))
              if (!isFirstInputDone()) {
                setFirstInputDone()
              }
            } else if (this.refreshSessionOnUndo) {
              this.$store.commit('refresh_session_on_undo', false)
              setFirstInputDone()
            }

            this.init() // refresh
            scrollToTop() // scroll to top on each input
          })
          .catch(err => {
            console.log(err)
            this.updateInputState(false)

            if (err.response && err.response.status === 400) {
              // handle input field validation errors
              const error = getErrorData(err)
              console.log(error)

              if (error.data && error.data.refresh_data) {
                this.warning = 'Sorry, your session has expired.  Please refresh the page and try again.'
              } else if (this.needsInputFields()) {
                console.log('needs input fields')
                this.$store.commit('error', error.errors)
              } else if (this.selectedInput && this.selectedInput.type === InputType.Checkout) {
                this.$store.commit('checkout_error', error.errors)
              } else {
                this.$store.commit('critical_error', err)
                this.init()
              }
            } else if (err.response && err.response.status === 402) { // payment required
              // popup payment form - input then needs to be resubmitted with input data {card: payment info}
              this.$store.commit('error', err) // remove this error and show a friendly message along with the payment form
            } else if (err.response && err.response.status === 422) { // non-critical error (more like a warning)
              this.$store.commit('error', err)
            } else {
              this.$store.commit('critical_error', err)
              this.init()
            }
          })
      }
    },
    goBack: function(count = 0) {
      console.log('go back')

      if (this.session && this.session.is_step_preview) {
        return // ignore back button if this is a preview step
      }

      let canUndo = !this.submitting && this.session && this.session.can_undo

      if (canUndo) {
        if (count === 0) {
          history.back()
        }
        this.$store.commit('busy')
        this.$store.dispatch('undo', count ? { count: count } : {})
          .then((session) => {
            if (count > 0) {
              const slug = getTrimmedValue(session.step_slug)
              // 1️⃣ Replace current URL to clear forward entries
              this.$router.replace({ name: 'session', params: { step: slug } })
              this.$router.push({ name: 'session', params: { step: slug } })
              this.$router.replace({ name: 'session', params: { step: slug } })
            }
            this.$store.commit('remove_steps_from_step_stack', count)
            this.$store.commit('clear_messages')
            this.$store.commit('increment_undo_count')
            this.init() // refresh
          })
          .catch(err => {
            console.log(err)
            if (!err.response || err.response.status !== 422) {
              this.$store.commit('critical_error', err)
            }
          })
      } else {
        this.$store.commit('increment_undo_count')
        this.$store.commit('clear_messages')
        // Clear step stack if we can't undo
        if (!this.submitting) {
          this.$store.commit('clear_step_stack')
        }
        this.init() // refresh
        if (this.canInitializeEnhancedContent()) {
          this.initializeEnhanceContent()
        }
      }
    },
    init: function() {
      console.log('session init')
      this.showLoader()
      this.$store.commit('set_input_state', InputState.Unknown)
      this.inputType = InputType.Choice
      this.selectedInput = null
      this.selectedIntent = null
      this.userSelectedInput = null
      this.updatedItem = null
      this.isItemSelection = false
      this.submitting = false
      this.inputData = null
      this.subscriptionOnly = false
      this.quantityType = QuantityType.Any
      this.statusMessages = []
      this.itemSelectionState = InputState.Unknown
      this.checkForVariants = false
      this.selectedCampaignProduct = null
      this.warning = null

      if (!this.session || this.session.step_type !== InputType.Confirmation) {
        this.selectedItem = null
      }

      if (this.session) {
        this.callCenterPopupContent = this.session.call_center_popup

        if (this.session.is_first_step) {
          this.$store.commit('clear_selected_items')
        }

        if (this.session.valid_inputs.length === 1) {
          // if there's only 1 valid input, select it by default
          const autoSelectedInput = this.session.valid_inputs[0]
          this.inputType = autoSelectedInput.type

          if (this.session.step_type !== InputType.Confirmation) {
            this.selectedInput = autoSelectedInput
            if (isItemSelectionInput(this.selectedInput)) {
              this.isItemSelection = true
            }
          }

          this.updateInputState(false)
        } else if (this.session.valid_inputs.length > 1) {
          // multiple inputs - get the type
          this.inputType = this.session.valid_inputs[0].type
        }

        if (this.isCustomerPortal || (this.session.side_menu_inputs && this.session.side_menu_inputs.length)) {
          this.selectedInput = this.session.side_menu_inputs.find((menu) => menu.type === this.session.step_type ||
            (this.session.step_type === InputType.CustomerPortalOrderDetail && menu.type === InputType.CustomerPortalOrders))
          this.updateInputState(false)
        }

        if (this.isOrderTracking && this.session.selected_item) {
          this.$store.commit('busy')
          this.$store.dispatch('load_fulfillments')
            .then(_ => {
              this.$store.commit('ready')
              setTimeout(() => this.$bvModal.show('fulfillment-tracking-modal'))
            })
        }

        // show/hide the lifeline popup or contact form
        if (this.session.show_lifeline_contact) {
          this.$bvModal.hide('lifeline')
          setTimeout(() => this.$bvModal.show('contact'))
        } else if (this.session.is_lifeline_active) {
          this.$bvModal.hide('contact')
          setTimeout(() => this.$bvModal.show('lifeline'))
        }

        this.populateBreadcrumbs(this.session)
      }

      this.hideLoader()
    },
    initializeEnhanceContent: function() {
      const _this = this

      // Function to perform the actual initialization
      const performInitialization = () => {
        const pageContents = document.querySelectorAll('.page-content')

        // If no content found, return false to indicate initialization didn't happen
        if (pageContents.length === 0) {
          return false
        }

        pageContents.forEach(pageContent => {
          if (pageContent) {
            this.hasSlotContent = true
            // skip in case of step preview
            if (_this.session && !_this.session.is_step_preview) {
              // setup path inputs
              const pathInputs = Array.from(pageContent.querySelectorAll("[ctype='path-input']")) || []
              pathInputs.forEach(element => {
                const elemType = element.getAttribute('elemtype')
                if (elemType === 'button' || elemType === 'link') {
                  let validInputs = _this.session.valid_inputs
                  // if step is a popup step then consider valid inputs from both background and popupsession
                  if (_this.session.is_popup && _this.popupSession) {
                    let popupInputs = _this.popupSession.valid_inputs || []
                    validInputs = validInputs.concat(popupInputs)
                  }
                  const hasInput = validInputs.some((validInput) => validInput.input_id === +element.getAttribute('input'))
                  if (hasInput) {
                    if (elemType === 'link') {
                      element.removeAttribute('target')
                      element.removeAttribute('href')
                    }
                    if (!element.callback) {
                      element.addEventListener('click', element.callback = (e) => {
                        const inputEl = e.target.closest('a[elemtype=button]') || e.target.closest('a[elemtype=link]')
                        const input = validInputs.find((validInput) => validInput.input_id === +inputEl.getAttribute('input'))
                        _this.selectInput(input)
                        return false
                      })
                    }
                  } else {
                    element.remove()
                  }
                }
              })
            }

            // setup back buttons
            const backBtns = Array.from(pageContent.querySelectorAll("[elemtype='back-button']")) || []
            backBtns.forEach(element => {
              element.addEventListener('click', (e) => {
                this.goBack()
                return false
              })
            })
          }
        })

        pageContents.forEach(pageContent => {
          if (pageContent.style) {
            pageContent.style.setProperty('display', 'block')
          }
        })

        return true
      }

      // Try immediate initialization
      if (performInitialization()) {
        return
      }

      // If content not available, use MutationObserver to wait for it
      let attempts = 0
      const maxAttempts = 50 // Maximum 5 seconds (50 * 100ms)

      const waitForContent = () => {
        if (performInitialization()) {
          return // Success, content initialized
        }

        attempts++
        if (attempts < maxAttempts) {
          // Use requestAnimationFrame for better performance
          requestAnimationFrame(() => {
            setTimeout(waitForContent, 100)
          })
        }
      }

      // Start waiting for content
      this.$nextTick(() => {
        waitForContent()
      })
    },
    setAltImg: function(event) {
      event.target.src = getPlaceholderImageUrl('large')
    },
    getSessionTitle(session) {
      let title = null
      if (session && this.firstLogin && session.welcome_message && session.welcome_message.length) {
        title = session.welcome_message
      } else if (session && !session.enhanced_mode) {
        title = session.label
      }
      return updateFontSizes(title)
    },
    getSessionContent(session) {
      if (session && session.content) {
        return updateFontSizes(session.content)
      }
      return null
    },
    getSessionSubTitle(session) {
      if (session && session.subtitle) {
        return updateFontSizes(session.subtitle)
      }
      return null
    },
    isButtonGroup: function(session) {
      if (session) {
        let input = session.valid_inputs[0]
        if (input) {
          return (input.type === InputType.Choice)
        }
      }
      return false
    },
    isSelectedProductRequired(session) {
      if (session && !this.variantSelected) {
        for (const input of session.valid_inputs) {
          const keys = Object.keys(input.input_fields)

          if (keys && keys.length) {
            const key = keys[0]
            const field = input.input_fields[key]

            if (field.type === InputType.Product) {
              this.subscriptionOnly = !!field.subscription_only
              this.quantityType = field.quantity_type || QuantityType.Any
              return true
            }
          }
        }
      }
      this.variantSelected = false
      return false
    },
    showFinalSubmitButton: function(session) {
      return session && !session.hide_all_buttons
    },
    canInitializeEnhancedContent() {
      return this.session && (this.session.enhanced_mode ||
        (this.session.is_popup && this.popupSession && this.popupSession.enhanced_mode))
    }
  },
  watch: {
    '$store.state.session': function(to, from) {
      if (this.canInitializeEnhancedContent()) {
        const _this = this
        setTimeout(() => {
          _this.initializeEnhanceContent()
        }, 50)
      }
    }
  },
  created() {
    if (this.$route.query['language']) {
      selectLanguage(this.$route.query['language'].toString().toLowerCase())
    }
    this.init()
    if (this.isFAQs) {
      this.$router.replace({name: 'faq-topics'})
    }
    if (this.isProductInfo) {
      this.$router.replace({name: 'products'})
    }
    if (this.isMainMenu && !this.isAlreadySessionUrl) {
      this.$router.replace({name: 'session'})
    }
  },
  updated() {
    if (this.isFAQs) {
      setTimeout(() => {
        if (window.location.pathname !== '/topics') {
          this.$router.replace({name: 'faq-topics'})
        }
      }, 100)
    }
    if (this.isProductInfo) {
      this.$router.replace({
        name: 'products',
        params: {
          input: this.selectedInput,
          title: this.session.label,
          subtitle: this.session ? this.session.subtitle : '',
          submit: this.selectInput,
          back: this.goBack
        }
      })
    }

    if (this.isMainMenu && !this.isAlreadySessionUrl) {
      this.$router.replace({name: 'session'})
    }

    if (!this.hasSlotContent && this.canInitializeEnhancedContent()) {
      this.initializeEnhanceContent()
    }

    recalculateFontSizes()
  },
  mounted: function mounted() {
    if (this.canInitializeEnhancedContent()) {
      this.initializeEnhanceContent()
    }
  },
  beforeDestroy() {
    this.$root.$emit('toggle-processing-gifs', null)
  }
}
</script>

<style scoped>
</style>
